import type { GridRow, GridRowsPage } from '../contracts/public-api';
import { NULL_BACKEND_SENTINEL, NULL_DISPLAY_KEY } from './grid-orchestration.constants';
import type { GroupPaginationState } from './grid-orchestration.types';
import { buildPrimaryCountsFromTreePaths } from './grid-orchestration.utils';

/** Result of merging tree-derived primary counts into pagination state. */
export interface PrimaryCountsMergeResult {
  readonly pagination: Map<string, GroupPaginationState>;
  readonly elementsTotalCount: number;
}

/**
 * Merges primary-level bucket counts rolled up from a complete group summary tree.
 * Fills in buckets missing from a partial paginated summary and corrects totals.
 */
export function mergePrimaryCountsIntoPagination(
  pagination: Map<string, GroupPaginationState>,
  nodes: ReadonlyArray<{ path: string; count: number }>
): PrimaryCountsMergeResult {
  const primaryCounts: Record<string, number> = buildPrimaryCountsFromTreePaths(
    nodes,
    NULL_BACKEND_SENTINEL,
    NULL_DISPLAY_KEY
  );
  const next: Map<string, GroupPaginationState> = new Map<string, GroupPaginationState>(pagination);

  for (const [key, count] of Object.entries(primaryCounts)) {
    const existing: GroupPaginationState | undefined = next.get(key);
    if (existing !== undefined) {
      if (existing.totalCount !== count) {
        next.set(key, { ...existing, totalCount: count });
      }
    } else {
      next.set(key, {
        nodes: [],
        endCursor: null,
        hasNextPage: true,
        totalCount: count,
        loading: false,
        pagesLoaded: 0
      });
    }
  }

  let elementsTotalCount: number = 0;
  for (const count of Object.values(primaryCounts)) {
    elementsTotalCount += count;
  }

  return { pagination: next, elementsTotalCount };
}

/** Applies merged tree totals to the elements response while preserving loaded nodes. */
export function applyElementsTotalCount(current: GridRowsPage | null, totalCount: number): GridRowsPage {
  return {
    nodes: current?.nodes ?? new Array<GridRow>(),
    totalCount
  };
}
